import { useEffect } from 'react';

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  // Remove any prior tag with this id (route change), then re-inject
  const prev = document.head.querySelector(`script[type="application/ld+json"][data-key="${id}"]`);
  if (prev) prev.remove();
  if (!data) return;
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.setAttribute('data-key', id);
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
};

/**
 * Lightweight per-route SEO hook.
 * Sets <title>, meta description, canonical, Open-Graph tags, and optional
 * JSON-LD structured data (single object or array).
 * Set `noindex: true` for invalid product/category pages so Googlebot
 * doesn't index the SPA fallback shell — even though the HTTP status is 200.
 */
export default function useSEO({ title, description, image, canonical, type = 'website', jsonLd, noindex = false } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, 'property');
      setMeta('twitter:description', description);
    }
    if (title) {
      setMeta('og:title', title, 'property');
      setMeta('twitter:title', title);
    }
    if (image) {
      setMeta('og:image', image, 'property');
      setMeta('twitter:image', image);
    }
    setMeta('og:type', type, 'property');
    setMeta('twitter:card', 'summary_large_image');

    // robots: default to index,follow. noindex flag flips to noindex,follow
    // so link equity still passes through to the canonical URL.
    setMeta('robots', noindex ? 'noindex,follow' : 'index,follow');

    const url = canonical || (typeof window !== 'undefined' ? window.location.href : undefined);
    if (url) {
      setLink('canonical', url);
      setMeta('og:url', url, 'property');
    }

    // Structured data (JSON-LD) — pass an object or an array of objects.
    if (jsonLd !== undefined) {
      // Strip any build-time prerendered JSON-LD (data-key="seo-route") so
      // React's runtime schema wins — otherwise a stale prerendered
      // Product/CollectionPage schema (baked in at deploy time) would sit
      // alongside the fresh one, and crawlers would pick the first match.
      document.head
        .querySelectorAll('script[type="application/ld+json"][data-key="seo-route"]')
        .forEach((n) => n.remove());
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((d, i) => setJsonLd(`route-${i}`, d));
      // Clean up any extra tags from the previous route
      let i = arr.length;
      while (document.head.querySelector(`script[type="application/ld+json"][data-key="route-${i}"]`)) {
        setJsonLd(`route-${i}`, null);
        i += 1;
      }
    }
  }, [title, description, image, canonical, type, jsonLd, noindex]);
}
